import React, {useCallback, useEffect, useState} from "react";
import {Box} from "@mui/system";
import {CircularProgress, Divider, TextField, Typography} from "@mui/material";
import _ from "lodash";
import {organizationSettingsService} from "_services";
import {alertMessages} from "_constants";
import {Auth} from "components/Authorization";
import {SnackBar} from "components/common";
import ActionButton from "../../common/ActionButton";
import styles from "../OrganizationSettings/organizationMainStyles";

const label = {
    HEADING: 'Generated Replies',
    DESCRIPTION: 'Default prompt used for generating replies for organizations without their own prompt.',
    PROMPT: 'Default prompt',
    PROMPT_PLACEHOLDER: "Describe the style of expected replies, ...",
    LAST_UPDATED: 'Last updated',
    LOAD_FAILED: 'The default prompt could not be loaded.',
};

// Buttons fit their labels instead of filling the row
const buttonSx = {width: "auto", minWidth: "110px"};

const promptInputSx = {
    "& textarea": {
        fontFamily: "larsseitRegular",
        fontSize: "15px",
    },
};

// Hints sit right under the section divider, like the prompt editor
const hintSx = {...styles.infoItem, mt: 0};

function formatDate(value) {
    if (!value) {
        return "—";
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function handleRequestError(error) {
    if (_.get(error, "response.status") === 401) {
        Auth.processLogout();
        return;
    }
    const message = _.get(error, "response.data.message") || alertMessages.GENERIC_ERROR;
    SnackBar.showMessage(message);
}

export default function DefaultSuggestedResponses() {
    const [loading, setLoading] = useState(true);
    const [prompt, setPrompt] = useState(null);
    const [text, setText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const showPrompt = useCallback((loadedPrompt) => {
        setPrompt(loadedPrompt);
        setText(loadedPrompt.text);
    }, []);

    useEffect(() => {
        const loadPrompt = async () => {
            try {
                const res = await organizationSettingsService.getDefaultSuggestedResponsePrompt();
                showPrompt(res.data);
            } catch (e) {
                handleRequestError(e);
            } finally {
                setLoading(false);
            }
        };
        loadPrompt();
    }, [showPrompt]);

    const trimmedText = text.trim();
    const isDirty = prompt != null && text !== prompt.text;

    const handleUpdate = async () => {
        setSubmitting(true);
        try {
            const res = await organizationSettingsService.updateDefaultSuggestedResponsePrompt(trimmedText);
            SnackBar.showMessage("Prompt saved.");
            showPrompt(res.data);
        } catch (e) {
            handleRequestError(e);
        } finally {
            setSubmitting(false);
        }
    };

    const renderBody = () => {
        if (loading) {
            return (
                <Box sx={styles.bodyLoading}>
                    <CircularProgress sx={styles.bodyLoadingSpinner}/>
                </Box>
            );
        }
        if (!prompt) {
            return <Typography sx={hintSx}>{label.LOAD_FAILED}</Typography>;
        }
        return (
            <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                <Typography sx={{...hintSx, mb: 2}}>{label.DESCRIPTION}</Typography>
                <TextField
                    label={label.PROMPT}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={label.PROMPT_PLACEHOLDER}
                    multiline
                    minRows={8}
                    fullWidth
                    disabled={submitting}
                    error={isDirty && !trimmedText}
                    helperText={isDirty && !trimmedText ? "Prompt cannot be blank." : " "}
                    // Keeps the label above the field, so the placeholder shows while it's empty and not focused
                    InputLabelProps={{shrink: true, sx: {fontFamily: "LarsseitMedium", fontSize: "14px"}}}
                    sx={promptInputSx}
                />
                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                    <Typography sx={{...styles.infoItem, m: 0}}>
                        {label.LAST_UPDATED}: {formatDate(prompt.updatedAt)}
                    </Typography>
                    <ActionButton
                        type="button"
                        label="Save"
                        action={handleUpdate}
                        disabled={submitting || !isDirty || !trimmedText}
                        loading={submitting}
                        withIcon={false}
                        sx={buttonSx}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={styles.sectionWrapper}>
            <Box component="h2" sx={{...styles.heading, mb: 0}}>
                {label.HEADING}
            </Box>
            <Divider orientation="horizontal" flexItem sx={styles.sectionDivider}/>
            {renderBody()}
        </Box>
    );
}
